/**
 * The race — a list of ETFs against SPY, one lane each, ranked by Strength —
 * as Sector Race draws it and as ETF Performance's Race view draws its list.
 * Built once; `SectorRaceWidget` and `PerfTableWidget` only frame it.
 *
 * Approved mock: `docs/sector-race-widget-mock.html`. The server sends every
 * period of the window (`qf/sector_race.py`), so the scrubber, the tail and
 * play read from that one reply and never ask again. The maths is
 * `lib/brief-race.ts`.
 *
 * Saved on the widget: D / W, the tail, the states shown. **The scrubbed
 * period is view state** — reopening, or switching D / W, shows the newest.
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties, type MouseEvent } from "react";
import type { BriefWidget } from "@qufolio/model";
import { fetchBriefRace, type RaceFreq, type RaceMember, type RacePayload } from "../../api/daily-brief";
import {
  DEFAULT_TAIL, MAX_TAIL, PLAY_MS, RACE_STATES, STATE_COLOR, STATE_LABEL, TAILS,
  arrowSize, changedState, countStates, ghosts, horsePos, nextTail, periodsInState, rankAt, shownStates, stateAt,
  tailStart, toggleShown, type RaceState, type Tail,
} from "../../lib/brief-race";
import { Chip, MUTED } from "./cells";
import { groupRows } from "./PerfTable";
import { useBriefData, type BriefData } from "./useBriefData";
import { NO_DRAG, useWidgetSetting } from "./WidgetCard";

// ── settings and data ────────────────────────────────────────────────────────

export interface RaceSettings {
  freq: RaceFreq;
  setFreq: (v: RaceFreq) => void;
  tail: Tail;
  setTail: (v: Tail) => void;
  shown: RaceState[];
  setShown: (v: RaceState[]) => void;
}

/** `freq`, `tail` and `shown` — widget settings, so they persist and follow preset-plus-yours like a move. */
export function useRaceSettings(widget: BriefWidget, onSettings?: (s: Record<string, unknown>) => void): RaceSettings {
  const [freq, setFreq] = useWidgetSetting<RaceFreq>(widget, "freq", "w", onSettings);
  const [tail, setTail] = useWidgetSetting<number>(widget, "tail", DEFAULT_TAIL, onSettings);
  const [shown, setShown] = useWidgetSetting<unknown>(widget, "shown", RACE_STATES, onSettings);
  return {
    freq: freq === "d" ? "d" : "w",
    setFreq,
    tail: (TAILS as readonly number[]).includes(tail) ? (tail as Tail) : DEFAULT_TAIL,
    setTail,
    shown: shownStates(shown),
    setShown,
  };
}

/** `null` symbols: the 11 sector funds. An empty list, or `enabled` false, asks nothing. */
export function useRaceData(symbols: readonly string[] | null, freq: RaceFreq, enabled = true): BriefData<RacePayload> {
  const key = !enabled || (symbols && symbols.length === 0) ? null : `race:${symbols ? symbols.join(",") : "sectors"}:${freq}`;
  return useBriefData<RacePayload>(key, () => fetchBriefRace(symbols, freq));
}

/** The title bar's label, beside the title (WidgetCard's `subtitle`), as the mock has it. */
export function RaceSubtitle() {
  return <span style={LABEL}>vs SPY</span>;
}

/** The title bar's controls: D | W as one segmented control, and the tail chip cycling 8 → 13 → 4. */
export function RaceControls({ settings }: { settings: RaceSettings }) {
  return (
    <>
      <style>{SEG_CSS}</style>
      <span className="qf-race-seg" style={SEG}>
        <Chip on={settings.freq === "d"} onClick={() => settings.setFreq("d")} title="Daily bars">D</Chip>
        <Chip on={settings.freq === "w"} onClick={() => settings.setFreq("w")} title="Weekly bars">W</Chip>
      </span>
      <span style={LABEL}>tail</span>
      <Chip
        onClick={() => settings.setTail(nextTail(settings.tail))}
        title="Periods drawn behind each horse"
        label={`Tail, ${settings.tail} periods`}
      >
        {settings.tail} ▾
      </Chip>
    </>
  );
}

// ── the race ─────────────────────────────────────────────────────────────────

export interface RaceViewProps {
  data: RacePayload;
  settings: RaceSettings;
  /** Rank within each category, under its header row (ETF Performance grouped by Category). */
  groups?: { category: (symbol: string) => string | undefined; order: readonly string[] };
  /** Below 6 columns, or on a phone: no Δrank column and no "Strength 100" in the header. */
  narrow?: boolean;
  /** Opens `SYM/SPY` in the linked chart; absent, a row is not a link. */
  onOpenSymbol?: (symbol: string) => void;
}

const ROW_H = 32;
const CAT_H = 24;

interface Lane {
  m: RaceMember;
  rank: number | undefined;
  /** Places gained over the tail, within the lane's own race. */
  delta: number | null;
  of: number;
}

/**
 * Below this width the race drops its Δrank column and the scale's middle
 * label. **Measured, not taken from the grid columns** (owner, 2026-10-18: the
 * ETF race clipped): a 6-column widget on a small screen is narrower than a
 * 4-column one on a large one, and the fixed columns then pushed the track —
 * and the leaders at its right end — out of sight.
 */
export const RACE_NARROW_BELOW = 560;

/** The element's width, followed as it is resized; 0 until measured (and in jsdom). */
function useWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([entry]) => setWidth(entry?.contentRect.width ?? 0));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width] as const;
}

export function RaceView({ data, settings, groups, narrow: narrowProp, onOpenSymbol }: RaceViewProps) {
  const [rootRef, width] = useWidth<HTMLDivElement>();
  const narrow = !!narrowProp || (width > 0 && width < RACE_NARROW_BELOW);
  const { dates, members, freq } = data;
  const last = dates.length - 1;
  // The scrubber starts at the longest tail, so a tail is always full.
  const min = Math.max(0, Math.min(MAX_TAIL, last));
  // The scrubbed period is held as its *date*, not an index: every refresh is the
  // last 65 periods, so a new period slides the window and an index would move with it.
  const [pinned, setPinned] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const [hover, setHover] = useState<{ symbol: string; x: number; y: number } | null>(null);
  const t = indexOf(dates, pinned, min, last);
  const tail = settings.tail;
  const suffix = freq === "w" ? "w" : "d";

  // A new frequency is a different window: back to the newest.
  useEffect(() => {
    setPinned(null);
    setPlaying(false);
  }, [settings.freq]);

  useEffect(() => {
    if (!playing) return;
    const id = setInterval(
      () => setPinned((d) => dates[Math.min(last, indexOf(dates, d, min, last) + 1)] ?? null),
      PLAY_MS,
    );
    return () => clearInterval(id);
  }, [playing, dates, min, last]);

  useEffect(() => {
    if (playing && t >= last) {
      setPlaying(false);
      setPinned(null);
    }
  }, [playing, t, last]);

  const play = () => {
    if (playing) return setPlaying(false);
    if (t >= last) setPinned(dates[min] ?? null);
    setPlaying(true);
  };

  // One race per category (or one in all), each ranked on its own.
  const sections = useMemo(() => {
    const parts: [string | null, RaceMember[]][] = groups
      ? groupRows(members, (m) => groups.category(m.symbol), groups.order)
      : [[null, [...members]]];
    return parts.map(([name, ms]) => {
      const now = rankAt(ms, t);
      const then = rankAt(ms, tailStart(t, tail));
      const lanes: Lane[] = ms.map((m) => {
        const r = now.get(m.symbol), r0 = then.get(m.symbol);
        return { m, rank: r, delta: r === undefined || r0 === undefined ? null : r0 - r, of: now.size };
      });
      // Ranked first, 1 = furthest ahead; a lane with no point this period keeps its place below.
      lanes.sort((a, b) => (a.rank ?? Infinity) - (b.rank ?? Infinity));
      return { name, lanes };
    });
  }, [members, groups, t, tail]);

  // Where each lane and header sits: transforms, so a re-sort slides.
  const slots: { key: string; y: number; header?: string; lane?: Lane }[] = [];
  let y = 0;
  for (const s of sections) {
    if (s.name !== null) {
      slots.push({ key: `cat:${s.name}`, y, header: s.name });
      y += CAT_H;
    }
    for (const lane of s.lanes) {
      slots.push({ key: lane.m.symbol, y, lane });
      y += ROW_H;
    }
  }

  const counts = countStates(members, t);
  const shown = new Set(settings.shown);
  // `minmax(0, 1fr)`: the track gives way, never the columns beside it — a bare
  // `1fr` will not shrink below its content and pushed the row past the widget.
  const cols = narrow ? "18px 44px minmax(0, 1fr) 96px" : "22px 52px minmax(0, 1fr) 44px 124px";
  const hovered = hover ? slots.find((s) => s.lane?.m.symbol === hover.symbol)?.lane : undefined;

  const onMove = (symbol: string) => (e: MouseEvent<HTMLDivElement>) => {
    const box = e.currentTarget.parentElement?.getBoundingClientRect();
    // Kept inside the widget: a card past the right edge is clipped by the card's overflow.
    const x = e.clientX - (box?.left ?? 0) + 12;
    setHover({ symbol, x: box && box.width > 0 ? Math.min(x, box.width - TIP_W - 4) : x, y: e.clientY - (box?.top ?? 0) });
  };

  if (last < 0 || members.length === 0) return <div style={MUTED}>No race to draw.</div>;

  return (
    <div ref={rootRef} style={ROOT}>
      <style>{RACE_CSS}</style>
      <div style={CHIPS}>
        {RACE_STATES.map((s) => (
          <button
            key={s}
            type="button"
            className={NO_DRAG}
            aria-pressed={shown.has(s)}
            data-testid={`race-chip-${s}`}
            onClick={() => settings.setShown(toggleShown(settings.shown, s))}
            style={shown.has(s) ? CHIP : { ...CHIP, opacity: 0.4 }}
          >
            <span style={{ ...SWATCH, background: STATE_COLOR[s] }} />
            {STATE_LABEL[s]} <span style={MUTED}>{counts[s]}</span>
          </button>
        ))}
      </div>

      <div style={{ ...GRID_ROW, ...HEAD, gridTemplateColumns: cols }}>
        <span>#</span>
        <span />
        <span style={SCALE}>
          <span>◀ behind SPY</span>
          {!narrow && <span>Strength 100</span>}
          <span>ahead ▶</span>
        </span>
        {!narrow && <span>Δrank</span>}
        <span>state</span>
      </div>

      <div style={{ position: "relative", height: y }} onMouseLeave={() => setHover(null)}>
        {slots.map((slot) =>
          slot.header !== undefined ? (
            <div key={slot.key} className="qf-race-lane" style={{ ...CAT, transform: `translateY(${slot.y}px)` }}>
              {slot.header}
            </div>
          ) : (
            <LaneRow
              key={slot.key}
              lane={slot.lane!}
              y={slot.y}
              t={t}
              tail={tail}
              cols={cols}
              narrow={!!narrow}
              suffix={suffix}
              shown={shown}
              onMove={onMove(slot.lane!.m.symbol)}
              onOpen={onOpenSymbol ? () => onOpenSymbol(`${slot.lane!.m.symbol}/SPY`) : undefined}
            />
          ),
        )}
        {hover && hovered && <HoverCard lane={hovered} t={t} tail={tail} suffix={suffix} x={hover.x} y={hover.y} link={!!onOpenSymbol} />}
      </div>

      <div style={FOOT}>
        <Chip onClick={play} title={playing ? "Pause" : "Play the window"} label={playing ? "Pause" : "Play"}>
          {playing ? "❚❚" : "▶"}
        </Chip>
        <input
          type="range"
          className={NO_DRAG}
          aria-label="Period"
          min={min}
          max={last}
          value={t}
          onChange={(e) => {
            setPlaying(false);
            const v = Number(e.target.value);
            setPinned(v >= last ? null : (dates[v] ?? null));
          }}
          style={SLIDER}
        />
        <span style={DATE} data-testid="race-date">{(freq === "w" ? "wk " : "") + dates[t]}</span>
        <span style={{ ...LIVE, visibility: data.live && t === last ? "visible" : "hidden" }}>● live</span>
      </div>
    </div>
  );
}

function LaneRow({
  lane, y, t, tail, cols, narrow, suffix, shown, onMove, onOpen,
}: {
  lane: Lane;
  y: number;
  t: number;
  tail: number;
  cols: string;
  narrow: boolean;
  suffix: string;
  shown: ReadonlySet<RaceState>;
  onMove: (e: MouseEvent<HTMLDivElement>) => void;
  onOpen?: () => void;
}) {
  const { m, rank, delta } = lane;
  const strength = m.strength[t];
  const momentum = m.momentum[t];
  const state = stateAt(m, t);
  const color = state ? STATE_COLOR[state] : "var(--qf-text-muted)";
  const past = ghosts(m, t, tail);
  const here = typeof strength === "number" ? horsePos(strength) : null;
  // The faint line from the oldest dot to now.
  const oldest = past[0]?.pos ?? here;
  const a = here === null || oldest === null ? null : Math.min(oldest, here);
  const b = here === null || oldest === null ? null : Math.max(oldest, here);
  // A lane with no point this period is neither shown nor hidden by a chip.
  const dim = !!state && !shown.has(state);

  return (
    <div
      className="qf-race-lane"
      data-testid="race-lane"
      data-symbol={m.symbol}
      data-state={state ?? ""}
      data-dim={dim ? "true" : "false"}
      role={onOpen ? "button" : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onClick={onOpen}
      onKeyDown={
        onOpen
          ? (e) => {
              if (e.key !== "Enter" && e.key !== " ") return;
              // Space would scroll the board.
              e.preventDefault();
              onOpen();
            }
          : undefined
      }
      onMouseMove={onMove}
      style={{
        ...GRID_ROW, ...LANE, gridTemplateColumns: cols, transform: `translateY(${y}px)`,
        opacity: dim ? 0.3 : 1, cursor: onOpen ? "pointer" : "default",
      }}
    >
      <span style={RANK} data-testid="race-rank">{rank ?? "–"}</span>
      <span style={SYM}>{m.symbol}</span>
      <span style={TRACK}>
        <span style={ZERO} />
        {a !== null && b !== null && <span style={{ ...TAIL_LINE, left: `${a}%`, width: `${b - a}%`, background: color }} />}
        {past.map((g) => (
          <span
            key={g.t}
            data-testid="race-ghost"
            style={{ ...GHOST, left: `${g.pos}%`, background: color, opacity: g.opacity }}
          />
        ))}
        {here !== null && typeof momentum === "number" && (
          <span
            className="qf-race-horse"
            data-testid="race-horse"
            style={{ ...HORSE, left: `${here}%`, backgroundColor: color, ...(changedState(m, t) ? RING : null) }}
          >
            <i style={{ ...ARROW, fontSize: arrowSize(momentum) }}>{momentum >= 100 ? "▲" : "▼"}</i>
          </span>
        )}
      </span>
      {!narrow && (
        <span
          style={{ ...DELTA, color: delta ? (delta > 0 ? "var(--qf-positive)" : "var(--qf-negative)") : "var(--qf-text-muted)" }}
          data-testid="race-delta"
        >
          {delta === null || delta === 0 ? "–" : delta > 0 ? `▲${delta}` : `▼${-delta}`}
        </span>
      )}
      <span style={{ ...STATE, color }}>{state ? `${STATE_LABEL[state]} ${periodsInState(m, t)}${suffix}` : "—"}</span>
    </div>
  );
}

function HoverCard({
  lane, t, tail, suffix, x, y, link,
}: {
  lane: Lane;
  t: number;
  tail: number;
  suffix: string;
  x: number;
  y: number;
  link: boolean;
}) {
  const { m, rank, delta, of } = lane;
  const state = stateAt(m, t);
  const s = m.strength[t], mo = m.momentum[t];
  const fmt = (v: number | null | undefined) => (typeof v === "number" ? v.toFixed(2) : "—");
  return (
    <div style={{ ...TIP, left: Math.max(0, x), top: y + 14 }} role="tooltip" data-testid="race-tip">
      <b>{m.symbol}</b> / SPY ·{" "}
      {state ? (
        <span style={{ color: STATE_COLOR[state] }}>{STATE_LABEL[state]} {periodsInState(m, t)}{suffix}</span>
      ) : (
        <span style={MUTED}>no bar</span>
      )}
      <br />
      <span style={MUTED}>Strength</span> {fmt(s)} &nbsp; <span style={MUTED}>Momentum</span> {fmt(mo)}
      <br />
      <span style={MUTED}>Rank</span> {rank ?? "–"} of {of} &nbsp; <span style={MUTED}>over {tail}{suffix}</span>{" "}
      {delta === null ? "–" : delta > 0 ? `+${delta}` : delta}
      {link && (
        <>
          <br />
          <span style={MUTED}>Click to open {m.symbol}/SPY</span>
        </>
      )}
    </div>
  );
}

/** The slide and the horse's move, and none of it under reduced motion. */
const RACE_CSS =
  ".qf-race-lane{transition:transform .45s ease,opacity .3s}" +
  ".qf-race-horse{transition:left .45s ease,background-color .3s}" +
  ".qf-race-lane[role=button]:hover{background:var(--qf-row-hover)}" +
  "@media (prefers-reduced-motion: reduce){.qf-race-lane,.qf-race-horse{transition:none}}";

/** D | W joined, as the mock's `.seg`: square inner corners, one shared border. `!important` beats Chip's inline radius. */
const SEG_CSS =
  ".qf-race-seg>button:first-child{border-top-right-radius:0!important;border-bottom-right-radius:0!important}" +
  ".qf-race-seg>button+button{margin-left:-1px;border-top-left-radius:0!important;border-bottom-left-radius:0!important}" +
  ".qf-race-seg>button[aria-pressed=true]{position:relative;z-index:1}";
const SEG: CSSProperties = { display: "inline-flex" };

/** The index of a scrubbed date in this window: the newest when none, the scrubber's start when it slid out. */
function indexOf(dates: readonly string[], date: string | null, min: number, last: number): number {
  if (date === null) return last;
  const i = dates.indexOf(date);
  return i < 0 ? min : Math.max(min, Math.min(i, last));
}

const ROOT: CSSProperties = {
  display: "flex", flexDirection: "column", minHeight: "100%", position: "relative", minWidth: 0, overflowX: "hidden",
};
const LABEL: CSSProperties = { color: "var(--qf-text-muted)", fontSize: 13, fontWeight: 400 };
const CHIPS: CSSProperties = { display: "flex", gap: 4, flexWrap: "wrap", paddingBottom: 4 };
const CHIP: CSSProperties = {
  borderWidth: 1, borderStyle: "solid", borderColor: "var(--qf-border)", borderRadius: 5, padding: "1px 8px",
  background: "var(--qf-surface-alt)", color: "var(--qf-text)", cursor: "pointer", fontSize: 13,
  display: "inline-flex", alignItems: "center", gap: 5, whiteSpace: "nowrap",
};
const SWATCH: CSSProperties = { width: 10, height: 10, borderRadius: "50%", display: "inline-block" };
const GRID_ROW: CSSProperties = { display: "grid", gap: 10, alignItems: "center" };
const HEAD: CSSProperties = {
  color: "var(--qf-text-muted)", fontSize: 12, padding: "6px 0 4px", borderBottom: "1px solid var(--qf-border)",
};
const SCALE: CSSProperties = { display: "flex", justifyContent: "space-between", whiteSpace: "nowrap", overflow: "hidden" };
const LANE: CSSProperties = {
  position: "absolute", left: 0, right: 0, top: 0, height: ROW_H, boxSizing: "border-box",
  borderBottom: "1px solid color-mix(in srgb, var(--qf-border) 55%, transparent)",
};
const CAT: CSSProperties = {
  position: "absolute", left: 0, right: 0, top: 0, height: CAT_H, boxSizing: "border-box", display: "flex",
  alignItems: "flex-end", paddingBottom: 3, fontWeight: 600, fontSize: 12, color: "var(--qf-text-secondary)",
  textTransform: "uppercase", letterSpacing: "0.03em", borderBottom: "1px solid var(--qf-border)",
};
const RANK: CSSProperties = { color: "var(--qf-text-muted)", textAlign: "right", fontVariantNumeric: "tabular-nums" };
const SYM: CSSProperties = { fontWeight: 600 };
const TRACK: CSSProperties = {
  position: "relative", height: 22,
  background:
    "linear-gradient(90deg, color-mix(in srgb, var(--qf-negative) 6%, transparent) 50%, " +
    "color-mix(in srgb, var(--qf-positive) 6%, transparent) 50%)",
};
const ZERO: CSSProperties = {
  position: "absolute", top: -5, bottom: -5, left: "50%", borderLeft: "1.5px dashed var(--qf-text-muted)",
  opacity: 0.7, zIndex: 1,
};
const TAIL_LINE: CSSProperties = { position: "absolute", top: "50%", height: 2, transform: "translateY(-50%)", opacity: 0.35 };
const GHOST: CSSProperties = {
  position: "absolute", top: "50%", width: 5, height: 5, margin: "-2.5px 0 0 -2.5px", borderRadius: "50%",
};
const HORSE: CSSProperties = {
  position: "absolute", top: "50%", width: 16, height: 16, margin: "-8px 0 0 -8px", borderRadius: "50%",
  display: "grid", placeItems: "center", zIndex: 2,
};
const RING: CSSProperties = { boxShadow: "0 0 0 2px var(--qf-background), 0 0 0 3.5px var(--qf-text)" };
const ARROW: CSSProperties = { fontStyle: "normal", lineHeight: 1, color: "var(--qf-background)" };
const DELTA: CSSProperties = { fontSize: 13, fontVariantNumeric: "tabular-nums" };
const STATE: CSSProperties = { fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };
const FOOT: CSSProperties = {
  display: "flex", alignItems: "center", gap: 8, padding: "6px 0", marginTop: "auto", position: "sticky", bottom: -6,
  background: "var(--qf-surface)", borderTop: "1px solid var(--qf-border)", fontSize: 13,
};
const SLIDER: CSSProperties = { flex: 1, minWidth: 60, accentColor: "var(--qf-accent)" };
const DATE: CSSProperties = { color: "var(--qf-text-muted)", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };
const LIVE: CSSProperties = { color: "var(--qf-positive)", fontSize: 12, whiteSpace: "nowrap" };
const TIP_W = 230;
const TIP: CSSProperties = {
  position: "absolute", zIndex: 20, width: TIP_W, background: "var(--qf-surface)", border: "1px solid var(--qf-border)",
  borderRadius: 8, padding: "8px 10px", fontSize: 13, pointerEvents: "none",
  boxShadow: "0 8px 24px color-mix(in srgb, var(--qf-background) 70%, transparent)",
};
